import { useQueryClient } from '@tanstack/react-query';
import { Building2, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { selectedBranch, setSelectedBranch, useSelectedBranch } from '@/lib/branch';
import { useTranslation } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { ActionMenu, ActionMenuItem, ActionMenuLabel } from './ui/action-menu';

export function BranchSwitcher({ className }: { className?: string }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const selectedId = useSelectedBranch();
  const { t } = useTranslation('shell');
  const branches = user?.branches ?? [];

  if (!user || branches.length < 2) return null;

  const current = selectedBranch(user, selectedId);
  const mixedCurrencies = new Set(branches.map((branch) => branch.currency)).size > 1;

  const choose = (branchId: string | null) => {
    if (branchId === selectedId) return;
    setSelectedBranch(branchId);
    queryClient.clear();
  };

  return (
    <ActionMenu
      label={t('branch.label')}
      align="start"
      className="w-[20rem] max-w-[calc(100vw-2rem)]"
      trigger={
        <button
          type="button"
          aria-label={t('branch.label')}
          className={cn(
            'group flex h-9 max-w-full items-center gap-2 rounded-full border border-border/80 bg-muted/40 ps-1.5 pe-3 text-sm transition-colors focus-ring',
            'hover:border-primary/40 hover:bg-primary/5 data-[state=open]:border-primary/50 data-[state=open]:bg-primary/10',
            className,
          )}
        >
          <span
            className={cn(
              'grid h-6 w-6 shrink-0 place-items-center rounded-full',
              'bg-primary text-primary-foreground',
            )}
          >
            <Building2 className="h-3.5 w-3.5" aria-hidden />
          </span>
          <span className="flex min-w-0 flex-col items-start leading-none">
            <span className="hidden text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:block">
              {t('branch.working_in')}
            </span>
            <span className="max-w-[12rem] truncate font-medium sm:mt-0.5">
              {current?.name ?? branches[0]?.name}
            </span>
          </span>
          {current && <span className="hidden shrink-0 rounded bg-background px-1 font-mono text-[11px] text-muted-foreground md:block">{current.code}</span>}
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" aria-hidden />
        </button>
      }
    >
      <ActionMenuLabel>{t('branch.pick')}</ActionMenuLabel>
      {branches.map((branch) => (
        <ActionMenuItem
          key={branch.id}
          selected={branch.id === current?.id}
          onSelect={() => choose(branch.id)}
          icon={<Building2 />}
          hint={
            <span className="font-mono">
              {branch.code}
              {mixedCurrencies ? ` · ${branch.currency}` : ''}
            </span>
          }
        >
          {branch.name}
        </ActionMenuItem>
      ))}
    </ActionMenu>
  );
}
